import { useEffect, useRef, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Activity, ArrowLeftRight, BarChart3, BellRing, CalendarX2, ChevronUp, Compass,
  FileSignature, FileText, History, LogOut, MessageSquareWarning, PanelLeft,
  Send, SlidersHorizontal, TrendingUp, UserCircle2, Users,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useT } from '@/i18n';
import { useAuthStore } from '@/stores/auth.store';
import { authApi } from '@/features/auth/auth.api';
import { agentMetricsApi } from '@/features/agent-metrics/agent-metrics.api';
import { useBugReportsBadge } from '@/features/bug-reports/useBugReports';
import { NotificationsBell } from '@/components/NotificationsBell';
import { ChatButton } from '@/features/chat/ChatPanel';
import { BugReportButton } from '@/components/BugReportButton';
import { LangToggle } from '@/components/chassis/LangToggle';
import { MobileNavDrawer } from '@/components/MobileNavDrawer';

/**
 * F5-T3 · La cáscara del admin (mockup «Admin 01 — Cáscara y sidebar»).
 *
 * Sidebar 264px fijo por defecto, colapsable a riel de 76px (la preferencia
 * se recuerda por usuario, localStorage con try/catch); once entradas en
 * cinco grupos + las transicionales que T6 fusiona (no se orfaniza una
 * página que el CEO usa hoy). Para supervisor, las cuatro admin-only
 * DESAPARECEN del árbol — no se deshabilitan («una entrada que no lleva a
 * ninguna parte es peor que una entrada ausente»; muere el 403 silencioso
 * del viejo Bug-Sidebar-No-Role-Filter). El chip «solo admin» del dibujo es
 * andamio de revisión: acá no se dibuja.
 *
 * «Mi perfil» y «Cerrar sesión» NO son entradas: viven en el menú de usuario
 * al pie (una acción, un lugar — logout salió de la barra superior).
 * Barra superior 68px: toggle del riel · título de la ventana · indicador de
 * dato en vivo (solo en Monitoreo, donde el poll de 5s existe de verdad) ·
 * campana · chat (el dibujo no lo dibuja ni lo eliminó — se conserva hasta
 * su firma, acta E-23) · «Reportar un problema» (F-18 firmada) · idioma.
 */

const RAIL_KEY = 'pathway.adminRail';

function readRailPref(): boolean {
  try { return localStorage.getItem(RAIL_KEY) === '1'; } catch { return false; }
}

interface ChromeItem {
  to: string;
  end?: boolean;
  labelKey: string;
  icon: LucideIcon;
  adminOnly?: boolean;
  /** F5-T8 (contrato Supervisor): entrada que SOLO el supervisor ve —
   *  Preautorizaciones, «lo único que sólo él firma». Para el admin no se
   *  dibuja (su árbol de T3 está cerrado). */
  supervisorOnly?: boolean;
  /** Entrada que T6 fusiona — vive acá para no orfanizar la página. */
  transitional?: boolean;
  badge?: number;
}

/** Los cinco grupos con sus entradas — la única fuente (desktop Y cajón). */
function useChromeGroups(): Array<{ labelKey: string; items: ChromeItem[] }> {
  const role = useAuthStore((s) => s.user?.role);
  const bugBadge = useBugReportsBadge();
  const bugs = bugBadge.data?.unresolvedTotal ?? 0;

  const groups: Array<{ labelKey: string; items: ChromeItem[] }> = [
    {
      labelKey: 'adminChrome.group.operacion',
      items: [
        { to: '/admin', end: true, labelKey: 'adminChrome.nav.live', icon: Activity },
        // F5-T8 (contrato Supervisor, sección 02): Preautorizaciones — la
        // única entrada propia del rol, 2ª de Operación en el dibujo. Hoy
        // monta la pantalla placeholder de construcción: la bandeja de firma
        // real (y su contador vivo de badge) necesita server.
        { to: '/admin/preauthorizations', labelKey: 'f5t8.sup.preauth', icon: FileSignature, supervisorOnly: true },
        { to: '/admin/missed-bookings', labelKey: 'adminChrome.nav.citas', icon: CalendarX2 },
        { to: '/admin/reassign', labelKey: 'adminChrome.nav.reasign', icon: ArrowLeftRight },
      ],
    },
    {
      labelKey: 'adminChrome.group.personas',
      items: [
        { to: '/admin/team', labelKey: 'adminChrome.nav.equipo', icon: Users, adminOnly: true },
        { to: '/admin/agents-progress', labelKey: 'adminChrome.nav.progreso', icon: TrendingUp },
        { to: '/admin/uploader-team', labelKey: 'admin.uploaderTeam.nav', icon: Send, transitional: true },
      ],
    },
    {
      labelKey: 'adminChrome.group.navegacion',
      items: [
        { to: '/admin/navigation', labelKey: 'adminChrome.nav.control', icon: Compass },
      ],
    },
    {
      labelKey: 'adminChrome.group.datos',
      items: [
        { to: '/admin/batches', labelKey: 'adminChrome.nav.lotes', icon: FileText, adminOnly: true },
        { to: '/admin/reports', labelKey: 'adminChrome.nav.reportes', icon: BarChart3 },
        { to: '/admin/uploaders-activity', labelKey: 'admin.uploadersActivity.nav', icon: TrendingUp, transitional: true },
      ],
    },
    {
      labelKey: 'adminChrome.group.sistema',
      items: [
        { to: '/admin/settings', labelKey: 'adminChrome.nav.config', icon: SlidersHorizontal, adminOnly: true },
        // F5-R6c (Admin 14): el grupo «Chat y notificaciones» de Configuración
        // gana su ventana propia — entrada nueva del árbol, solo admin.
        { to: '/admin/chat-notifications', labelKey: 'f5r6c.nav', icon: BellRing, adminOnly: true },
        { to: '/admin/audit', labelKey: 'adminChrome.nav.auditoria', icon: History, adminOnly: true },
        { to: '/admin/bug-reports', labelKey: 'adminChrome.nav.bugs', icon: MessageSquareWarning, badge: bugs },
      ],
    },
  ];

  // Para supervisor las admin-only DESAPARECEN del árbol, no se deshabilitan.
  // F5-T8: y las supervisor-only (Preautorizaciones) solo existen en SU árbol.
  return groups
    .map((g) => ({
      ...g,
      items: g.items.filter((i) => (!i.adminOnly || role === 'admin') && (!i.supervisorOnly || role === 'supervisor')),
    }))
    .filter((g) => g.items.length > 0);
}

/** La lista de navegación — la misma en el sidebar de escritorio y el cajón. */
export function AdminNavList({ rail }: { rail: boolean }): JSX.Element {
  const t = useT();
  const visible = useChromeGroups();
  return (
    <nav className="flex-1 overflow-y-auto overflow-x-hidden px-3 pb-3 pt-1.5" aria-label={t('adminChrome.navLabel')}>
      {visible.map((g) => (
        <div key={g.labelKey}>
          {!rail && (
            <p className="mt-[18px] mb-1.5 px-3 font-head text-[10px] font-bold tracking-[.16em] uppercase text-nav-faint">
              {t(g.labelKey as Parameters<typeof t>[0])}
            </p>
          )}
          {g.items.map((item) => (
            <SidebarItem key={item.to} item={item} rail={rail} />
          ))}
        </div>
      ))}
    </nav>
  );
}

export function AdminChrome({ children }: { children: React.ReactNode }): JSX.Element {
  const t = useT();
  const location = useLocation();
  const role = useAuthStore((s) => s.user?.role);
  const [rail, setRail] = useState(readRailPref);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // El cajón se cierra al elegir destino (mockup móvil).
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  const toggleRail = (): void => {
    setRail((r) => {
      try { localStorage.setItem(RAIL_KEY, r ? '0' : '1'); } catch { /* per-viewer */ }
      return !r;
    });
  };

  const title = pageTitle(location.pathname, t);
  const roleWord = role === 'supervisor' ? t('adminChrome.role.supervisor') : t('adminChrome.role.admin');

  return (
    <div className="flex min-h-screen bg-nav-soft">
      {/* ---- Sidebar (desktop). En móvil, el cajón monta LA MISMA lista. */}
      {/* Sticky de ALTO PANTALLA: el menú de usuario queda anclado al borde
          visible (la 1ª captura lo mostró hundido al fondo de una página de
          4600px — el aside estiraba con el contenido). El nav interno
          scrollea solo. */}
      <aside
        className={cn(
          'hidden lg:flex flex-col flex-none bg-white border-r-[1.5px] border-nav-card overflow-hidden',
          'lg:sticky lg:top-0 lg:h-screen self-start',
          'transition-[width] duration-200 ease-[cubic-bezier(.4,0,.2,1)] motion-reduce:transition-none',
          rail ? 'w-[76px]' : 'w-[264px]',
        )}
        data-t3="admin-sidebar"
      >
        <div className={cn('flex items-center gap-[11px] h-[68px] flex-none border-b-[1.5px] border-nav-card', rail ? 'px-[22px]' : 'px-5')}>
          <img src="/mark.png" alt="" className="h-8 w-auto flex-none" />
          {!rail && (
            <span className="min-w-0 whitespace-nowrap">
              <span className="block font-head text-[13.5px] font-extrabold leading-tight text-nav-navy">Pathway</span>
              <span className="block font-head text-[10px] font-bold tracking-[.16em] uppercase text-nav-slate">{roleWord}</span>
            </span>
          )}
        </div>

        <AdminNavList rail={rail} />

        <UserMenu rail={rail} roleWord={roleWord} />
      </aside>

      <MobileNavDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        ariaLabel={t('adminChrome.navLabel')}
        title="Pathway"
      >
        <div className="flex flex-col min-h-0">
          <AdminNavList rail={false} />
          {/* El menú de usuario baja al pie del cajón (mockup móvil). */}
          <UserMenu rail={false} roleWord={roleWord} />
        </div>
      </MobileNavDrawer>

      {/* ---- Columna derecha: barra superior + contenido */}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="sticky top-0 z-20 flex items-center gap-[11px] bg-white border-b-[1.5px] border-nav-card px-5 lg:pl-6 h-[68px] flex-none min-w-0">
          {/* El MISMO botón: en escritorio colapsa el riel; en móvil abre el
              cajón (mockup: «abierto por el mismo botón de la barra»). */}
          <button
            type="button"
            onClick={toggleRail}
            aria-label={t('adminChrome.railToggle')}
            title={t('adminChrome.railToggle')}
            className="hidden lg:inline-flex items-center justify-center w-[44px] h-[44px] flex-none rounded-lg text-nav-slate hover:bg-nav-hair hover:text-nav-navy transition-colors"
          >
            <PanelLeft className="h-[18px] w-[18px]" aria-hidden />
          </button>
          <button
            type="button"
            onClick={() => setDrawerOpen(true)}
            aria-label={t('adminChrome.navLabel')}
            className="lg:hidden inline-flex items-center justify-center w-[44px] h-[44px] flex-none rounded-lg text-nav-slate hover:bg-nav-hair hover:text-nav-navy transition-colors"
          >
            <PanelLeft className="h-[18px] w-[18px]" aria-hidden />
          </button>

          <h2 className="m-0 min-w-[80px] truncate font-head text-[19px] font-bold tracking-[-0.01em] text-nav-navy">
            {title}
          </h2>
          {/* El cambio de ventana se anuncia (mockup: aria-live polite). */}
          <span aria-live="polite" className="sr-only">{title}</span>

          {location.pathname === '/admin' && <LivePill />}

          <div className="ml-auto flex items-center gap-0.5">
            <NotificationsBell />
            <ChatButton />
            <BugReportButton />
          </div>
          <div className="w-px h-[26px] bg-nav-card flex-none" aria-hidden />
          <LangToggle />
        </header>

        <div className="flex-1 min-w-0 px-4 py-4 sm:px-8 sm:py-7">
          {children}
        </div>
      </div>
    </div>
  );
}

function SidebarItem({ item, rail }: { item: ChromeItem; rail: boolean }): JSX.Element {
  const t = useT();
  const label = t(item.labelKey as Parameters<typeof t>[0]);
  return (
    <NavLink
      to={item.to}
      end={item.end}
      title={label}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-[11px] w-full box-border h-[44px] mb-0.5 rounded-lg font-head text-[13.5px] transition-colors active:scale-[0.98]',
          rail ? 'px-[17px]' : 'px-3',
          'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-nav-teal',
          isActive
            ? 'bg-nav-navy text-white font-bold'
            : 'text-nav-slate font-semibold hover:bg-nav-hair hover:text-nav-navy',
        )
      }
    >
      {({ isActive }) => (
        <>
          <item.icon className="h-[18px] w-[18px] flex-none" aria-hidden />
          {!rail && <span className="flex-1 min-w-0 text-left truncate">{label}</span>}
          {!rail && item.badge !== undefined && item.badge > 0 && (
            <span
              className={cn(
                'flex-none inline-block rounded-full px-2 py-0.5 font-head text-[11px] font-bold tabular-nums border',
                isActive
                  ? 'bg-white/15 text-white border-transparent'
                  : 'bg-nav-hair text-nav-slate border-nav-card',
              )}
            >
              {item.badge}
            </span>
          )}
        </>
      )}
    </NavLink>
  );
}

/**
 * El menú de usuario al pie: avatar con siglas, nombre y rol; Mi perfil y
 * Cerrar sesión. Escape cierra y devuelve el foco al botón (mockup).
 */
function UserMenu({ rail, roleWord }: { rail: boolean; roleWord: string }): JSX.Element {
  const t = useT();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const clearSession = useAuthStore((s) => s.clearSession);
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        setOpen(false);
        btnRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const handleLogout = async (): Promise<void> => {
    try {
      await authApi.logout();
    } catch {
      // igual se limpia local
    } finally {
      clearSession();
      navigate('/login', { replace: true });
    }
  };

  const initials = (user?.fullName ?? '')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');

  return (
    <div className="relative flex-none p-3 border-t-[1.5px] border-nav-card">
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="true"
        aria-expanded={open}
        className={cn(
          'flex items-center gap-2.5 w-full box-border min-h-[44px] rounded-lg hover:bg-nav-hair transition-colors',
          rail ? 'p-1 justify-center' : 'px-2 py-1.5',
        )}
      >
        <span className="flex-none inline-flex items-center justify-center w-8 h-8 rounded-full bg-nav-navy text-white font-head text-[11.5px] font-bold tracking-[.02em]">
          {initials || '·'}
        </span>
        {!rail && (
          <>
            <span className="flex-1 min-w-0 text-left whitespace-nowrap overflow-hidden">
              <span className="block font-head text-[13px] font-bold leading-tight text-nav-navy truncate">
                {user?.fullName ?? ''}
              </span>
              <span className="block font-body text-[11.5px] text-nav-slate">{roleWord}</span>
            </span>
            <ChevronUp
              className={cn('h-4 w-4 flex-none text-nav-faint transition-transform duration-200', !open && 'rotate-180')}
              aria-hidden
            />
          </>
        )}
      </button>

      {open && (
        <div
          role="menu"
          className="absolute left-3 right-3 bottom-[calc(100%-4px)] min-w-[196px] bg-white border-[1.5px] border-nav-card rounded-[12px] shadow-[0_12px_30px_rgba(18,43,68,.14)] p-1.5 animate-fade-up motion-reduce:animate-none"
        >
          <button
            type="button"
            role="menuitem"
            onClick={() => { setOpen(false); navigate('/admin/profile'); }}
            className="flex items-center gap-2 w-full box-border min-h-[44px] px-3 rounded-lg font-head text-[13.5px] font-semibold text-nav-navy hover:bg-nav-hair text-left transition-colors"
          >
            <UserCircle2 className="h-[17px] w-[17px] flex-none" aria-hidden />
            {t('adminChrome.menu.profile')}
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={() => void handleLogout()}
            className="flex items-center gap-2 w-full box-border min-h-[44px] px-3 rounded-lg font-head text-[13.5px] font-semibold text-nav-navy hover:bg-nav-hair text-left transition-colors"
          >
            <LogOut className="h-[17px] w-[17px] flex-none" aria-hidden />
            {t('adminChrome.menu.signOut')}
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * El indicador de dato en vivo — SOLO en Monitoreo, donde el poll de 5s
 * existe de verdad (DailySnapshotPanel, query ['admin','dashboard','team']).
 * Dibujarlo en ventanas sin poll sería un «en vivo» que miente. Tres estados
 * del mockup: salvia latiendo · ámbar con la EDAD del dato · rojo detenido
 * con UN botón «Reintentar». Sin jerga de servidor.
 */
function LivePill(): JSX.Element | null {
  const t = useT();
  const qc = useQueryClient();
  // enabled:false — se SUSCRIBE al estado del cache sin disparar fetches
  // propios; el dueño del poll es el panel de monitoreo.
  const q = useQuery({
    queryKey: ['admin', 'dashboard', 'team'],
    queryFn: agentMetricsApi.teamDashboard,
    enabled: false,
  });
  const [, tick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => tick((n) => n + 1), 5_000);
    return () => clearInterval(id);
  }, []);

  if (!q.dataUpdatedAt && !q.isError) return null;
  const ageSec = Math.round((Date.now() - q.dataUpdatedAt) / 1000);

  if (q.isError) {
    return (
      <span className="inline-flex items-center gap-[7px] flex-none overflow-hidden bg-nav-redTint border border-nav-red rounded-full py-[5px] px-[11px] font-head text-[11.5px] font-bold text-nav-red whitespace-nowrap" data-t3="live-stopped">
        <span className="w-[7px] h-[7px] rounded-full bg-nav-red flex-none" aria-hidden />
        {t('adminChrome.live.stopped')}
        <button
          type="button"
          className="underline underline-offset-2 hover:opacity-80"
          onClick={() => void qc.invalidateQueries({ queryKey: ['admin', 'dashboard', 'team'] })}
        >
          {t('adminChrome.live.retry')}
        </button>
      </span>
    );
  }
  if (ageSec > 15) {
    return (
      <span className="inline-flex items-center gap-[7px] flex-none overflow-hidden bg-nav-amberTint border border-nav-amber rounded-full py-[5px] px-[11px] font-head text-[11.5px] font-bold text-nav-amberText whitespace-nowrap tabular-nums" data-t3="live-stale">
        <span className="w-[7px] h-[7px] rounded-full bg-nav-amber flex-none" aria-hidden />
        {t('adminChrome.live.stale', { s: ageSec })}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-[7px] flex-none overflow-hidden bg-nav-sageTint border border-nav-sage rounded-full py-[5px] px-[11px] font-head text-[11.5px] font-bold text-nav-sageText whitespace-nowrap tabular-nums" data-t3="live-ok">
      <span className="w-[7px] h-[7px] rounded-full bg-nav-sage flex-none animate-pulse motion-reduce:animate-none" aria-hidden />
      {t('adminChrome.live.ok')}
    </span>
  );
}

/** El título de la barra: la ventana activa, por su primer segmento. */
function pageTitle(pathname: string, t: ReturnType<typeof useT>): string {
  const seg = pathname.replace(/^\/admin\/?/, '').split('/')[0] ?? '';
  const MAP: Record<string, string> = {
    '': 'adminChrome.nav.live',
    'missed-bookings': 'adminChrome.nav.citas',
    'preauthorizations': 'f5t8.sup.preauth',
    'reassign': 'adminChrome.nav.reasign',
    'team': 'adminChrome.nav.equipo',
    'agents-progress': 'adminChrome.nav.progreso',
    'navigation': 'adminChrome.nav.control',
    'batches': 'adminChrome.nav.lotes',
    'reports': 'adminChrome.nav.reportes',
    'settings': 'adminChrome.nav.config',
    'chat-notifications': 'f5r6c.nav',
    'system-config': 'adminChrome.nav.config',
    'audit': 'adminChrome.nav.auditoria',
    'bug-reports': 'adminChrome.nav.bugs',
    'uploader-team': 'admin.uploaderTeam.nav',
    'uploaders-activity': 'admin.uploadersActivity.nav',
    'profile': 'adminChrome.menu.profile',
  };
  const key = MAP[seg] ?? 'adminChrome.nav.live';
  return t(key as Parameters<typeof t>[0]);
}
